import React, { useEffect, useRef, useState } from 'react';
import { Info, CaretDown, CaretUp } from '@phosphor-icons/react';

/**
 * Bandeau de statut réglementaire permanent
 * Affiché sur TOUTES les pages, mais réductible (collapsible) pour libérer de l'espace sur mobile.
 * La mention légale reste toujours accessible en un tap (jamais totalement supprimée => conformité MDR).
 * Sa hauteur réelle est exposée via --regulatory-h pour que le layout réserve l'espace (aucun chevauchement).
 */
const REG_COLLAPSE_KEY = 'hippocrate_reg_banner_collapsed';

export const RegulatoryBanner = () => {
  const ref = useRef(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(REG_COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = () => document.documentElement.style.setProperty('--regulatory-h', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(REG_COLLAPSE_KEY, String(next));
      } catch {
        /* noop */
      }
      return next;
    });
  };

  if (collapsed) {
    return (
      <div
        ref={ref}
        data-testid="regulatory-banner"
        className="fixed bottom-0 left-0 right-0 z-[60] bg-slate-900 border-t border-slate-800"
      >
        <button
          type="button"
          onClick={toggle}
          data-testid="regulatory-banner-expand"
          aria-label="Afficher la mention légale"
          className="w-full flex items-center justify-center gap-2 px-4 py-1.5 text-slate-400 hover:text-slate-200 transition-colors"
        >
          <Info size={14} weight="fill" className="text-blue-400 flex-shrink-0" />
          <span className="text-[11px] font-medium">Mention légale</span>
          <CaretUp size={12} weight="bold" className="flex-shrink-0" />
        </button>
      </div>
    );
  }

  return (
    <div 
      ref={ref}
      data-testid="regulatory-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] bg-slate-900 border-t border-slate-800 px-4 py-2"
    >
      <div className="max-w-7xl mx-auto flex items-start gap-2">
        <Info size={16} weight="fill" className="text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="flex-1 text-[11px] sm:text-xs text-slate-400 leading-relaxed">
          <span className="text-slate-300 font-medium">Outil documentaire et informationnel réservé aux professionnels de santé.</span>
          {' '}Ne constitue pas un dispositif médical (Règl. UE 2017/745) et ne se substitue en aucune manière au jugement clinique, à l'examen ni à la décision médicale.
        </p>
        <button
          type="button"
          onClick={toggle}
          data-testid="regulatory-banner-collapse"
          aria-label="Réduire la mention légale"
          className="flex-shrink-0 -mt-0.5 p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800 active:scale-95 transition-all"
        >
          <CaretDown size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
};

/**
 * Disclaimer affiché sous chaque réponse générée par l'IA
 * Conforme aux CGU et à l'AI Act (transparence)
 */
export const AIResponseDisclaimer = () => {
  return (
    <div 
      data-testid="ai-response-disclaimer"
      className="mt-6 p-4 bg-slate-900/50 border border-slate-800 rounded-lg space-y-3"
    >
      {/* Mention IA - AI Act */}
      <div className="flex items-start gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0"></div>
        <p className="text-xs text-amber-400/90 leading-relaxed">
          Contenu généré par un système d'intelligence artificielle, susceptible de comporter des erreurs, inexactitudes ou omissions. Le Service constitue une aide documentaire et non une source d'autorité.
        </p>
      </div>
      
      {/* Disclaimer principal */}
      <div className="flex items-start gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-2 flex-shrink-0"></div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Information générale et non individualisée, fournie à titre documentaire et de référence. Le professionnel de santé demeure seul et entièrement responsable de tout acte de diagnostic, de prescription, de soin ou de décision clinique. Vérifiez systématiquement toute information au regard des Sources officielles.
        </p>
      </div>
    </div>
  );
};

/**
 * Encart affiché quand une demande sort du périmètre documentaire
 * (hors_perimetre = true selon CGU Article 7.2)
 */
export const OutOfScopeNotice = () => {
  return (
    <div 
      data-testid="out-of-scope-notice"
      className="p-5 bg-slate-800/50 border border-slate-700 rounded-xl"
    >
      <div className="flex items-start gap-3">
        <Info size={24} weight="duotone" className="text-slate-400 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-medium text-slate-300 mb-2">Périmètre documentaire</h4>
          <p className="text-sm text-slate-400 leading-relaxed">
            Cette demande sort du périmètre documentaire de l'outil : analyse d'un cas patient identifié appelant une décision individualisée. Le Service ne produit pas de diagnostic, de pronostic ni de recommandation propres à un patient déterminé.
          </p>
        </div>
      </div>
    </div>
  );
};
